/**
 * Measurement utilities for the new data format with center/rotation structure
 */

import { apply3DRotationTo2D, projectPointOntoSegment } from './geometryUtils';
import { metersToFeetInches } from './floorPlanUtils';
import { calculateFixtureMeasurements } from './floorPlanFixtureMeasurements';
import { HIGHLIGHT_COLORS } from '../constants/highlightColors';
import { getMeasurementKey } from './measurementKeys';
import { lowestStoryRooms } from './storyOptions';
import {
  classifyMeasurementEdit,
  getOpeningMeasurementKey,
  isEditableEditMode,
} from './openingEdit';
import { buildRoomMeasuredOpeningIndex } from './openingSyncUtils';
import { calculateRecordMeasurements } from './planDimensionRecords';

/**
 * Get wall segments from room polygon
 * @param {Array} polygonPoints - Array of polygon corner points
 * @returns {Array} Array of wall segments
 */
export const getWallSegmentsFromPolygon = (polygonPoints) => {
  if (!polygonPoints || polygonPoints.length < 2) return [];

  const segments = [];
  const points = polygonPoints.map((point) => {
    if (point.x !== undefined && point.z !== undefined) {
      return [point.x, point.z];
    }
    return [point[0], point[1]];
  });

  for (let i = 0; i < points.length; i++) {
    const start = points[i];
    const end = points[(i + 1) % points.length];
    segments.push({
      start,
      end,
      index: i,
    });
  }

  return segments;
};

/**
 * Project a point onto a wall segment.
 *
 * Alias for the canonical projectPointOntoSegment (geometryUtils); kept so
 * existing importers of this module don't break.
 */
export const projectPointOntoWall = projectPointOntoSegment;

/**
 * The structure wall a room wall is a face of, as `{ center, rotation,
 * thickness }` — or null when it cannot be found, which leaves the room
 * plane as the only thing to measure to.
 */
const solidFor = (wall, structureWalls) => {
  if (!Array.isArray(structureWalls) || structureWalls.length === 0)
    return null;
  const id = wall.alignment?.structureWallId;
  const sw = structureWalls.find((w) => w.id === id || w.id === wall.id);
  const thickness = sw?.dimensions?.z ?? 0;
  if (!sw?.center || !(thickness > 0.001)) return null;
  return { center: sw.center, rotation: sw.rotation, thickness };
};

const roomPolygonPoints = (room) => {
  if (room?.floors?.[0]?.polygonCorners) return room.floors[0].polygonCorners;
  return room?.polygon || null;
};

/**
 * The room's wall segments, in the one derivation every consumer must share.
 *
 * Extracted from `calculateRoomMeasurementsV2` because the properties panel
 * needs the SAME segments to offer a centring datum that the generator will
 * later resolve. Two derivations would let the panel offer a wall id the
 * drawing cannot find.
 *
 * NOTE the id asymmetry, which is load-bearing for dimension records: wall
 * mode carries `wallId`, polygon mode does not — a polygon edge has no id to
 * name. A `{kind:'wall'}` datum is therefore unresolvable in polygon mode.
 *
 * @param {Object} room
 * @param {'wall'|'polygon'} measurementMode
 * @returns {Array} `{ start:[x,z], end:[x,z], index, wallId? }[]`
 */
export const roomWallSegments = (
  room,
  measurementMode = 'wall',
  structureWalls = null
) => {
  const fromPolygon = () => {
    const points = roomPolygonPoints(room);
    return points && points.length >= 3
      ? getWallSegmentsFromPolygon(points)
      : [];
  };

  if (measurementMode === 'polygon') return fromPolygon();
  if (!room?.walls || room.walls.length === 0) return fromPolygon();

  return room.walls
    .map((wall, index) => {
      if (!wall.center || !wall.dimensions || !wall.rotation) return null;
      const halfLength = wall.dimensions.x / 2;
      // Use consistent rotation method for all walls
      const wallDir = apply3DRotationTo2D(wall.rotation);
      return {
        start: [
          wall.center.x - wallDir[0] * halfLength,
          wall.center.z - wallDir[1] * halfLength,
        ],
        end: [
          wall.center.x + wallDir[0] * halfLength,
          wall.center.z + wallDir[1] * halfLength,
        ],
        index,
        // wallId lets the tiered emitter dispatch fixtures and openings
        // by their parentWallIds[] rather than scanning every wall.
        wallId: wall.id,
        // A click in the plan can land on the STRUCTURE wall rather than the
        // room's, and the two have different ids. A room wall points at its
        // structure wall here (`floorplanDataView.structureWallsForRoomWalls`
        // uses the same link the other way round), so carrying it lets a
        // datum naming either one resolve to this segment.
        structureWallId: wall.alignment?.structureWallId ?? null,
        // A ROOM wall is a zero-thickness PLANE, not a solid — measured on the
        // real corpus, 2309 of 2314 have `dimensions.z === 0`. The thing with
        // faces is the STRUCTURE wall behind it, so a dimension to "the wall"
        // has to read its centreline and thickness from there.
        //
        // That plane usually sits ON a face of the structure wall already
        // (91 of 100 walls in one real structure), where both readings agree.
        // Where it does NOT — 9 of 100, sitting on the CENTRELINE of a 216 mm
        // wall — trusting the plane put the dimension 108 mm inside the wall,
        // with visible wall still between it and the room.
        solid: solidFor(wall, structureWalls),
        thickness: wall.dimensions?.z ?? 0,
      };
    })
    .filter((segment) => segment !== null);
};

/**
 * Calculate measurements for a room using the new data format
 * @param {Object} room - Room object
 * @param {Array} objects - Array of objects in the room
 * @param {Array} doors - Array of doors
 * @param {Array} windows - Array of windows
 * @param {string} measurementMode - 'wall' or 'polygon'
 * @returns {Array} Array of measurement objects
 */
export const calculateRoomMeasurementsV2 = (
  room,
  objects,
  doors,
  windows,
  measurementMode = 'wall',
  openings = [],
  structureWalls = null
) => {
  const measurements = [];

  const wallSegments = roomWallSegments(room, measurementMode, structureWalls);

  // Fixture node-based measurements
  const allItems = [...objects, ...doors, ...windows, ...openings];
  const fixtureMeasurements = calculateFixtureMeasurements(
    room,
    allItems,
    wallSegments
  );

  // Stamp each editable opening measurement (width / position) with its lock
  // key so it shares the stretch measurementLocks store + 🔒 glyph via the
  // shared getMeasurementKey. `doors`/`windows` are resolved room openings
  // (id = room-copy id, structureOpeningId = canonical) — the index buildable
  // straight from them. Membership isn't needed for the key.
  const { copyIds, structureOpeningIdByCopyId } = buildRoomMeasuredOpeningIndex(
    {
      doors,
      windows,
    }
  );
  const EMPTY_MEMBERSHIP = new Map();
  for (const m of fixtureMeasurements) {
    const classification = classifyMeasurementEdit(m, {
      roomOpeningIds: copyIds,
      structureOpeningIdByCopyId,
      openingMembership: EMPTY_MEMBERSHIP,
    });
    const lockKey = getOpeningMeasurementKey(classification);
    if (lockKey) m.lockKey = lockKey;
    // Editable openings (width / position / gap) render in blue; fixture
    // group/inner detail and wall totals stay read-only (gray). Wall length
    // is editable only in the whole-structure view (see
    // calculateAllWallMeasurements), not here.
    m.editable = isEditableEditMode(classification.mode);
  }
  measurements.push(...fixtureMeasurements);

  // Stated dimension records (`room.dimensions[]`) — interior dimensions that
  // name their own datums rather than hanging off a wall. Appended, so the
  // generated chains above are untouched. Empty until a room states one.
  measurements.push(
    ...calculateRecordMeasurements(room, allItems, wallSegments)
  );

  return measurements;
};

/**
 * Calculate wall length measurements for all room walls (no-room-selected view).
 * Shows a single length measurement for every wall across all rooms.
 * Walls whose screen-space length is too short for a label are filtered out.
 * @param {Object} structureData - Full structure data with .rooms
 * @param {number} scale - Current zoom scale (pixels per meter)
 * @returns {Array} Array of measurement objects rendered by drawMeasurementsV2
 */
export const calculateAllWallMeasurements = (structureData, scale) => {
  const rooms = structureData?.rooms || [];
  if (rooms.length === 0) return [];

  const BASE_OFFSET = 0.5;
  const LABEL_PIXEL_WIDTH = 60;
  const minWallLength = LABEL_PIXEL_WIDTH / scale;
  const measurements = [];

  rooms.forEach((room) => {
    if (!room.walls || room.walls.length === 0) return;

    // Compute room center for offset direction — skip room if no polygon
    const polygonCorners = room.floors?.[0]?.polygonCorners;
    if (!polygonCorners || polygonCorners.length === 0) return;

    let cx = 0;
    let cz = 0;
    polygonCorners.forEach((pt) => {
      cx += pt.x !== undefined ? pt.x : pt[0];
      cz += pt.z !== undefined ? pt.z : pt[1];
    });
    const roomCenter = [cx / polygonCorners.length, cz / polygonCorners.length];

    room.walls.forEach((wall) => {
      if (!wall.center || !wall.dimensions || !wall.rotation) return;

      const halfLength = wall.dimensions.x / 2;
      const wallLength = wall.dimensions.x;
      if (wallLength < 0.01 || wallLength < minWallLength) return;

      const wallDir = apply3DRotationTo2D(wall.rotation);
      const start = [
        wall.center.x - wallDir[0] * halfLength,
        wall.center.z - wallDir[1] * halfLength,
      ];
      const end = [
        wall.center.x + wallDir[0] * halfLength,
        wall.center.z + wallDir[1] * halfLength,
      ];

      // Perpendicular direction
      const perpDir = [-wallDir[1], wallDir[0]];

      // Offset away from room center
      const wallMidpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
      const testPoint = [
        wallMidpoint[0] + perpDir[0] * 0.1,
        wallMidpoint[1] + perpDir[1] * 0.1,
      ];
      const distToCenter = Math.sqrt(
        (testPoint[0] - roomCenter[0]) ** 2 +
          (testPoint[1] - roomCenter[1]) ** 2
      );
      const wallDistToCenter = Math.sqrt(
        (wallMidpoint[0] - roomCenter[0]) ** 2 +
          (wallMidpoint[1] - roomCenter[1]) ** 2
      );
      const offsetDir =
        distToCenter > wallDistToCenter ? perpDir : [-perpDir[0], -perpDir[1]];

      const offsetStart = [
        start[0] + offsetDir[0] * BASE_OFFSET,
        start[1] + offsetDir[1] * BASE_OFFSET,
      ];
      const offsetEnd = [
        end[0] + offsetDir[0] * BASE_OFFSET,
        end[1] + offsetDir[1] * BASE_OFFSET,
      ];

      measurements.push({
        type: 'fixture-node',
        subtype: 'wall-length',
        // Wall length is editable in the whole-structure view via stretch.
        editable: true,
        start,
        end,
        distance: wallLength,
        label: metersToFeetInches(wallLength),
        offsetStart,
        offsetEnd,
        fromLabel: 'Wall end',
        toLabel: 'Wall end',
        fromObjectId: null,
        toObjectId: null,
        wallId: wall.id,
        roomId: room.id,
        extensionStartFrom: start,
        extensionStartTo: offsetStart,
        extensionEndFrom: end,
        extensionEndTo: offsetEnd,
      });
    });
  });

  return measurements;
};

/**
 * The wall lengths the whole-structure floor plan draws: every room of the
 * LOWEST floor shown (stacked stories share X/Z, so upper-floor strings would
 * land on the ground floor's). Each label is the click that opens the length
 * editor, so the floor plan's wall-length app actions offer exactly these.
 */
export const shownWallLengthMeasurements = (structureData, scale) =>
  calculateAllWallMeasurements(
    { ...structureData, rooms: lowestStoryRooms(structureData?.rooms) },
    scale
  );

/**
 * Hit-test a point against measurements — the dimension LINE or its value
 * LABEL, whichever the click lands on.
 *
 * The label is a real target, not a halo on the line: it is drawn
 * `labelGapWorld` outward of the line and is as big as its text, so at any
 * zoom below ~60 px/m the whole label sits beyond `threshold` and clicking the
 * number does nothing while clicking the thin line works. `layoutMeasurements`
 * stamps the drawn box on as `labelBox`; a measurement that never went through
 * layout simply falls back to the line test.
 *
 * @param {number} worldX - World X coordinate of click
 * @param {number} worldZ - World Z coordinate of click
 * @param {Array} measurements - Array of measurement objects
 * @param {number} threshold - Distance threshold in meters (default 0.15).
 *   Applies to the line only; the label box is hit exactly, with no halo, so
 *   it can't steal clicks from whatever sits beside it.
 * @returns {Object|null} Closest measurement within threshold, or null
 */
export const hitTestMeasurement = (
  worldX,
  worldZ,
  measurements,
  threshold = 0.15
) => {
  if (!measurements || measurements.length === 0) return null;

  let closest = null;
  let closestDist = Infinity;
  // Labels are painted in a second pass, OVER every dimension line, so a click
  // where a label covers another measurement's line belongs to the label —
  // whatever the distances say.
  let closestIsLabel = false;

  measurements.forEach((measurement) => {
    if (!measurement.offsetStart || !measurement.offsetEnd) return;

    const box = measurement.labelBox;
    if (box) {
      const rx = worldX - box.cx;
      const ry = worldZ - box.cy;
      const along = Math.abs(rx * box.ux + ry * box.uy);
      const perp = Math.abs(-rx * box.uy + ry * box.ux);
      if (along <= box.halfAlong && perp <= box.halfPerp) {
        if (!closestIsLabel) {
          closestDist = 0;
          closest = measurement;
          closestIsLabel = true;
        }
        return;
      }
    }
    if (closestIsLabel) return;

    const [sx, sy] = measurement.offsetStart;
    const [ex, ey] = measurement.offsetEnd;

    // Point-to-line-segment distance
    const dx = ex - sx;
    const dy = ey - sy;
    const lenSq = dx * dx + dy * dy;

    let dist;
    if (lenSq === 0) {
      // Degenerate segment
      dist = Math.sqrt((worldX - sx) ** 2 + (worldZ - sy) ** 2);
    } else {
      const t = Math.max(
        0,
        Math.min(1, ((worldX - sx) * dx + (worldZ - sy) * dy) / lenSq)
      );
      const projX = sx + t * dx;
      const projY = sy + t * dy;
      dist = Math.sqrt((worldX - projX) ** 2 + (worldZ - projY) ** 2);
    }

    if (dist < closestDist && dist <= threshold) {
      closestDist = dist;
      closest = measurement;
    }
  });

  return closest;
};

/**
 * Check if a measurement involves a specific object — strict direct match
 * against the measurement's actual endpoints (fromObjectId / toObjectId).
 * @param {Object} measurement - Measurement object
 * @param {string} objectId - Object ID to check
 * @returns {boolean}
 */
export const measurementInvolvesObject = (measurement, objectId) => {
  if (!measurement || !objectId) return false;
  return (
    measurement.fromObjectId === objectId || measurement.toObjectId === objectId
  );
};

/**
 * Check if a measurement directly involves any id in the given set
 * (fromObjectId / toObjectId). Used when one click target maps to
 * multiple equivalent ids — e.g. a structure-level window plus its room
 * copies linked via structureOpeningId.
 * @param {Object} measurement
 * @param {Set<string>} ids
 * @returns {boolean}
 */
export const measurementInvolvesAnyObject = (measurement, ids) => {
  if (!measurement || !ids || ids.size === 0) return false;
  return ids.has(measurement.fromObjectId) || ids.has(measurement.toObjectId);
};

/**
 * Draw measurements on the canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Array} measurements - Array of measurements
 * @param {number} scale - Current zoom scale
 * @param {boolean} showMeasurements - Whether to show measurements
 * @param {Object} selectionState - Optional selection state
 * @param {Object} selectionState.selectedMeasurement - Currently selected measurement
 * @param {Set<string>} selectionState.selectedObjectIds - Currently selected
 *   object plus its synonyms (e.g. structureOpeningId ↔ room-copy id), used
 *   for the involves-object highlight tiers.
 */
// Capped label scale: below this, label sizes stop growing so they don't
// become oversized at low zoom / in PDF exports.
const REFERENCE_SCALE = 40;

// End-cap tick half-length, in SCREEN px (converted to world via effectiveScale)
// so the ticks stay a small constant size and don't grow into adjacent rows
// when zoomed in.
const MEASUREMENT_CAP_PX = 4;

// The value label sits just OUTWARD of its dimension line, sized in screen px.
// `drawMeasurementLabel` paints it and `layoutMeasurements` stamps the same box
// onto the measurement as `labelBox`, so the hit test can accept a click on the
// text — what you can see is what you can click. Both derive it from here.
const LABEL_GAP_PX = 3; // clear space between the line and the box
const LABEL_BOX_PAD_PX = 2; // box height = fontPx + this

/** Distance (world m) from the dimension line to the label box's centre. */
const labelGapWorld = (fontPx, effectiveScale) =>
  (fontPx / 2 + LABEL_GAP_PX) / effectiveScale;

/** Half the label box's height, in world m. */
const labelHalfHeightWorld = (fontPx, effectiveScale) =>
  (fontPx + LABEL_BOX_PAD_PX) / 2 / effectiveScale;

// Base measurement colors signal whether a measurement can be edited by
// clicking it. Editable → blue; read-only → gray. Highlight colors
// (selection / involvement / preview) take precedence over both.
const EDITABLE_MEASUREMENT_COLOR = '#0066cc';
const READONLY_MEASUREMENT_COLOR = '#6b7280';
// In the stated-dimensions layer the read-only grey is the wrong signal:
// every dimension on screen is a stated one, so "read-only" distinguishes
// nothing, and grey over a plan faded to 28% reads as switched off rather
// than as the subject. A strong neutral ink, not the editable blue — these
// still are not click-to-edit.
const STATED_MEASUREMENT_COLOR = '#1f2937';

// A CENTRELINE mark: long-dash-short-dash, the drafting convention for an
// axis of symmetry. Not a measurement — it carries no distance and is never
// offset off an anchor, because its whole meaning is that it sits ON the
// centre it marks. See shared-lib/dimensioning/marks.js.
const CENTRELINE_COLOR = '#8a6d3b';
const CENTRELINE_DASH_PX = [12, 3, 3, 3];

const drawCentrelineMark = (ctx, mark, scale, fontPx = 10) => {
  const { from, to } = mark;
  if (!from || !to) return;
  const effectiveScale = Math.max(scale, REFERENCE_SCALE);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(from[0], from[1]);
  ctx.lineTo(to[0], to[1]);
  ctx.strokeStyle = CENTRELINE_COLOR;
  ctx.lineWidth = 0.4 / effectiveScale;
  ctx.setLineDash(CENTRELINE_DASH_PX.map((d) => d / effectiveScale));
  ctx.stroke();
  ctx.setLineDash([]);

  // The glyph stays upright whatever the line's angle — a rotated "CL" reads
  // as part of the drawing rather than as an annotation of it.
  //
  // Scaled UNIFORMLY, as `drawMeasurementLabel` does. A `scale(1, -1)` here
  // mirrored the text: a "C" survives that almost unchanged, but the "L" came
  // out upside down.
  ctx.translate(from[0], from[1]);
  ctx.scale(1 / effectiveScale, 1 / effectiveScale);
  ctx.font = `500 ${fontPx}px Arial, sans-serif`;
  ctx.fillStyle = CENTRELINE_COLOR;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(mark.label || 'CL', 0, 0);
  ctx.restore();
};

export const drawMeasurementsV2 = (
  ctx,
  measurements,
  scale,
  showMeasurements,
  selectionState = {}
) => {
  if (!showMeasurements || !measurements || measurements.length === 0) return;

  const {
    selectedMeasurement,
    selectedObjectIds,
    previewMap,
    lockedKeys,
    labelFontPx,
    emphasiseStated,
  } = selectionState;

  // Per-measurement draw state (color/stroke/preview/lock), computed once.
  const states = measurements.map((m) =>
    m.type === 'fixture-node'
      ? measurementDrawState(
          m,
          selectedMeasurement,
          selectedObjectIds,
          previewMap,
          lockedKeys,
          emphasiseStated
        )
      : null
  );

  ctx.save();
  // Two passes so a later measurement's line/tick can never paint over an
  // earlier measurement's label: draw ALL geometry first, then ALL labels.
  measurements.forEach((m, i) => {
    if (m.type === 'fixture-node') {
      drawMeasurementGeometry(ctx, m, scale, states[i]);
    } else if (m.type === 'dimension-mark') {
      drawCentrelineMark(ctx, m, scale, labelFontPx);
    }
  });
  measurements.forEach((m, i) => {
    if (m.type === 'fixture-node') {
      drawMeasurementLabel(ctx, m, scale, states[i], labelFontPx);
    }
  });
  ctx.restore();
};

// Stable key for preview/lock lookup — shared with the stretch module via
// measurementKeys.js (reads a pre-stamped opening lockKey when present).
const measurementKey = getMeasurementKey;

/**
 * Draw a fixture node-based measurement on canvas
 * Draws: extension lines (dashed), measurement line at offset, end caps, text label
 */
const measurementDrawState = (
  measurement,
  selectedMeasurement,
  selectedObjectIds,
  previewMap,
  lockedKeys,
  emphasiseStated = false
) => {
  const { offsetStart, offsetEnd } = measurement;

  // Three-tier selection coloring (clicked / direct-involves → PRIMARY,
  // sibling-involves → SECONDARY, stretch → PREVIEW).
  const isSelected =
    selectedMeasurement &&
    selectedMeasurement.offsetStart[0] === offsetStart[0] &&
    selectedMeasurement.offsetStart[1] === offsetStart[1] &&
    selectedMeasurement.offsetEnd[0] === offsetEnd[0] &&
    selectedMeasurement.offsetEnd[1] === offsetEnd[1];
  const hasSelection = selectedObjectIds && selectedObjectIds.size > 0;
  const directInvolves =
    hasSelection &&
    measurementInvolvesAnyObject(measurement, selectedObjectIds);
  const indirectInvolves =
    !directInvolves &&
    hasSelection &&
    Array.isArray(measurement.relatedObjectIds) &&
    measurement.relatedObjectIds.some((rid) => selectedObjectIds.has(rid));

  // Stretch-preview / lock state. previewMap[key] carries the OLD length.
  const key = measurementKey(measurement);
  const previewedOldMeters =
    previewMap && key && previewMap.has(key) ? previewMap.get(key) : null;
  const isPreviewed =
    previewedOldMeters !== null && previewedOldMeters !== undefined;
  const isLocked = lockedKeys && key && lockedKeys.has(key);

  // Base color signals editability: editable measurements (opening width /
  // position, wall length in structure view) are blue; read-only ones
  // (fixture dimensions, wall totals in room view) are gray. Selection /
  // involvement / preview highlights override this below.
  let color =
    emphasiseStated && measurement.subtype === 'dimension-record'
      ? STATED_MEASUREMENT_COLOR
      : measurement.editable
        ? EDITABLE_MEASUREMENT_COLOR
        : READONLY_MEASUREMENT_COLOR;
  let strokeMultiplier = 1;
  if (isSelected) {
    color = HIGHLIGHT_COLORS.PRIMARY;
    strokeMultiplier = 6;
  } else if (directInvolves) {
    color = HIGHLIGHT_COLORS.PRIMARY;
    strokeMultiplier = 3;
  } else if (indirectInvolves) {
    color = HIGHLIGHT_COLORS.SECONDARY;
    strokeMultiplier = 2;
  } else if (isPreviewed) {
    color = HIGHLIGHT_COLORS.PREVIEW;
    strokeMultiplier = 2;
  }

  return { color, strokeMultiplier, isPreviewed, previewedOldMeters, isLocked };
};

// PASS 1 geometry: extension lines (dashed), measurement line, end caps.
const drawMeasurementGeometry = (ctx, measurement, scale, state) => {
  const {
    offsetStart,
    offsetEnd,
    extensionStartFrom,
    extensionStartTo,
    extensionEndFrom,
    extensionEndTo,
  } = measurement;
  const { color, strokeMultiplier } = state;
  const effectiveScale = Math.max(scale, REFERENCE_SCALE);

  // Draw extension lines (dashed) from wall position to offset measurement line
  ctx.save();
  ctx.globalAlpha = 0.6;
  ctx.beginPath();
  // `extensionStartAt` / `extensionEndAt` carry the dotted line BACK to the
  // element when it does not reach the line the dimension was taken on — a
  // wall bounding a room that opens to the next one often stops short. They
  // are separate from `extension*From`, which the layout pass uses as the
  // anchor it offsets the dim line off; moving that would move the dimension.
  const startAt = measurement.extensionStartAt || extensionStartFrom;
  const endAt = measurement.extensionEndAt || extensionEndFrom;
  ctx.moveTo(startAt[0], startAt[1]);
  ctx.lineTo(extensionStartTo[0], extensionStartTo[1]);
  ctx.moveTo(endAt[0], endAt[1]);
  ctx.lineTo(extensionEndTo[0], extensionEndTo[1]);
  ctx.strokeStyle = color;
  ctx.lineWidth = (0.375 * strokeMultiplier) / effectiveScale;
  ctx.setLineDash([3 / effectiveScale, 3 / effectiveScale]);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // Draw measurement line at offset position
  ctx.beginPath();
  ctx.moveTo(offsetStart[0], offsetStart[1]);
  ctx.lineTo(offsetEnd[0], offsetEnd[1]);
  ctx.strokeStyle = color;
  ctx.lineWidth = (0.5 * strokeMultiplier) / effectiveScale;
  ctx.stroke();

  // Draw end caps perpendicular to the measurement line
  const angle = Math.atan2(
    offsetEnd[1] - offsetStart[1],
    offsetEnd[0] - offsetStart[0]
  );
  const perpAngle = angle + Math.PI / 2;
  const capLength = MEASUREMENT_CAP_PX / effectiveScale;

  // Start cap
  ctx.beginPath();
  ctx.moveTo(
    offsetStart[0] - Math.cos(perpAngle) * capLength,
    offsetStart[1] - Math.sin(perpAngle) * capLength
  );
  ctx.lineTo(
    offsetStart[0] + Math.cos(perpAngle) * capLength,
    offsetStart[1] + Math.sin(perpAngle) * capLength
  );
  ctx.stroke();

  // End cap
  ctx.beginPath();
  ctx.moveTo(
    offsetEnd[0] - Math.cos(perpAngle) * capLength,
    offsetEnd[1] - Math.sin(perpAngle) * capLength
  );
  ctx.lineTo(
    offsetEnd[0] + Math.cos(perpAngle) * capLength,
    offsetEnd[1] + Math.sin(perpAngle) * capLength
  );
  ctx.stroke();
};

// PASS 2 label: drawn just OUTWARD of the dimension line (so the line + ticks
// stay visible), on an opaque background. Runs after ALL geometry, so no later
// line/tick can paint over it.
const drawMeasurementLabel = (
  ctx,
  measurement,
  scale,
  state,
  labelFontPx = 12
) => {
  const {
    offsetStart,
    offsetEnd,
    label,
    extensionStartFrom,
    extensionStartTo,
  } = measurement;
  const { color, isPreviewed, previewedOldMeters, isLocked } = state;
  const effectiveScale = Math.max(scale, REFERENCE_SCALE);
  const angle = Math.atan2(
    offsetEnd[1] - offsetStart[1],
    offsetEnd[0] - offsetStart[0]
  );

  // Draw the label just OUTWARD of the dimension line (not centered on it), so
  // the line + end ticks stay visible and each value clearly belongs to its
  // segment — avoids the text covering its own (short) line at low zoom.
  const midX = (offsetStart[0] + offsetEnd[0]) / 2;
  const midY = (offsetStart[1] + offsetEnd[1]) / 2;
  let perpOutX = extensionStartTo[0] - extensionStartFrom[0];
  let perpOutY = extensionStartTo[1] - extensionStartFrom[1];
  const perpOutLen = Math.hypot(perpOutX, perpOutY) || 1;
  perpOutX /= perpOutLen;
  perpOutY /= perpOutLen;
  // Push the label center out by ~half a text height (+gap) so the box clears
  // the line. Uses effectiveScale to match the rendered (capped) label size.
  const gap = labelGapWorld(labelFontPx, effectiveScale);

  ctx.save();
  ctx.translate(midX + perpOutX * gap, midY + perpOutY * gap);
  ctx.scale(1 / effectiveScale, 1 / effectiveScale);

  // Rotate text to be parallel with measurement line
  let textAngle = angle;
  if (textAngle > Math.PI / 2 || textAngle < -Math.PI / 2) {
    textAngle += Math.PI;
  }
  ctx.rotate(textAngle);

  // Compose the displayed label:
  //   - lock glyph prefix if locked
  //   - old → new annotation if previewed
  let displayedLabel = label;
  if (isPreviewed) {
    displayedLabel = `${metersToFeetInches(previewedOldMeters)}  →  ${label}`;
  }
  if (isLocked) {
    displayedLabel = `🔒 ${displayedLabel}`;
  }

  // Draw label background
  ctx.font = `500 ${labelFontPx}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const textWidth = ctx.measureText(displayedLabel).width;
  const padding = 1;
  const boxH = labelFontPx + LABEL_BOX_PAD_PX;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
  ctx.fillRect(
    -textWidth / 2 - padding,
    -boxH / 2,
    textWidth + padding * 2,
    boxH
  );

  // Draw label text
  ctx.fillStyle = color;
  ctx.fillText(displayedLabel, 0, 0);

  ctx.restore();
};

// --- Measurement label layout (tiers + collision rows, screen-space) -------
const LAYOUT_PAD = 3; // px added to each label half-width (along-edge)
const LAYOUT_GAP = 4; // px min clear space between two labels in one row
const LAYOUT_BASE_OFFSET_PX = 28; // innermost dim line offset (screen px, at/below the zoom cap)
const LAYOUT_BASE_MIN_M = 0.3; // ...but never closer than this in world m, so the first line clears the (world-space) wall thickness when zoomed in
const LAYOUT_ROW_PITCH_PX = 12; // px added to fontPx for the per-slot pitch
const LAYOUT_TIER_TOL = 0.02; // m — same tier within this perpendicular distance
const LAYOUT_ANGLE_TOL = (5 * Math.PI) / 180; // rad — parallel within this
const LAYOUT_PERP_TOL = 0.05; // m — same wall line within this perpendicular distance

/**
 * Pure stagger primitive — shared between floor-plan and elevations.
 *
 * Given a list of pre-grouped items (each group already represents a single
 * dim-line direction + side), assign each item a `slot` index combining tier
 * (clustered by `tierDist`) and row (greedy 1D collision packing by
 * `[proj-half, proj+half]`). Slots within a group are 0-based and increase
 * outward; different groups are independent.
 *
 * Mutates each item: sets `item.slot`. Use a fresh array of items per call.
 *
 * @param {Array<{items: Array<{tierDist:number, proj:number, half:number}>}>} groups
 * @param {{tierTol:number, gap:number}} opts - both already in the same units
 *   as `tierDist` / `proj` / `half` (typically world-space, after
 *   width / effectiveScale).
 */
export const assignStaggerSlots = (groups, opts) => {
  const { tierTol, gap } = opts;
  for (const group of groups) {
    const sorted = [...group.items].sort((a, b) => a.tierDist - b.tierDist);
    const tiers = [];
    let current = null;
    for (const it of sorted) {
      if (!current || it.tierDist - current.dist > tierTol) {
        current = { dist: it.tierDist, items: [] };
        tiers.push(current);
      }
      current.items.push(it);
    }
    let slotBase = 0;
    for (const tier of tiers) {
      tier.items.sort((a, b) => a.proj - b.proj);
      const rowEnds = [];
      for (const it of tier.items) {
        const start = it.proj - it.half;
        const end = it.proj + it.half;
        let row = -1;
        for (let r = 0; r < rowEnds.length; r += 1) {
          if (rowEnds[r] + gap <= start) {
            row = r;
            break;
          }
        }
        if (row === -1) {
          row = rowEnds.length;
          rowEnds.push(end);
        } else {
          rowEnds[row] = end;
        }
        it.slot = slotBase + row;
      }
      slotBase += rowEnds.length;
    }
  }
};

/**
 * Lay out every floor-plan dimension label perpendicular to its wall: assigns
 * tiers AND collision rows as one screen-space slot sequence, so spacing tracks
 * the rendered (capped) label size at any zoom and tiers/rows can't overlap.
 * Pure: returns a new array. Each measurement is re-placed at
 * (BASE_OFFSET_PX + slot * ROW_PITCH_PX) / effectiveScale from its wall anchor.
 *
 * @param {Array} measurements - each needs offsetStart/offsetEnd, the wall
 *   anchors extensionStartFrom/extensionEndFrom, and label.
 * @param {{measureWidth?:(label:string)=>number, scale:number, fontPx?:number}} opts
 * @returns {Array}
 */
export const layoutMeasurements = (measurements, opts = {}) => {
  const { measureWidth, scale, fontPx = 12 } = opts;
  if (!Array.isArray(measurements) || measurements.length === 0 || !scale) {
    return measurements ?? [];
  }

  // A MARK is not a measurement: a centreline sits exactly on the centre it
  // marks, so offsetting it off an anchor — which is all this pass does —
  // would destroy its meaning. Pass them through, in place.
  if (measurements.some((m) => m?.type === 'dimension-mark')) {
    const marks = measurements.filter((m) => m?.type === 'dimension-mark');
    const rest = measurements.filter((m) => m?.type !== 'dimension-mark');
    const laidOut = layoutMeasurements(rest, opts);
    let next = 0;
    return measurements.map((m) =>
      m?.type === 'dimension-mark' ? m : laidOut[next++]
    );
  }
  const effectiveScale = Math.max(scale, REFERENCE_SCALE);
  const widthOf =
    typeof measureWidth === 'function'
      ? measureWidth
      : (label) => String(label ?? '').length * fontPx * 0.6;

  // 1. Derive per-measurement geometry + group by wall edge + side.
  const groups = [];
  const entries = measurements.map((m) => {
    const sx = m.offsetStart[0];
    const sy = m.offsetStart[1];
    let dx = m.offsetEnd[0] - sx;
    let dy = m.offsetEnd[1] - sy;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    if (dx < 0 || (dx === 0 && dy < 0)) {
      dx = -dx;
      dy = -dy;
    }
    const angle = Math.atan2(dy, dx);
    const wx = m.extensionStartFrom[0];
    const wy = m.extensionStartFrom[1];
    // Perpendicular (-dy, dx); pick the side the offset is on.
    const rawSide = -dy * (sx - wx) + dx * (sy - wy);
    const sideSign = rawSide < 0 ? -1 : 1;
    const sideX = -dy * sideSign;
    const sideY = dx * sideSign;
    const wallPerp = -dy * wx + dx * wy; // wall line position
    const tierDist = sideX * (sx - wx) + sideY * (sy - wy); // ≥ 0 from wall
    const midx = (sx + m.offsetEnd[0]) / 2;
    const midy = (sy + m.offsetEnd[1]) / 2;
    const proj = midx * dx + midy * dy;
    const half = (widthOf(m.label) / 2 + LAYOUT_PAD) / effectiveScale;

    let group = null;
    for (const g of groups) {
      let da = Math.abs(g.angle - angle);
      if (da > Math.PI / 2) da = Math.PI - da;
      if (
        da <= LAYOUT_ANGLE_TOL &&
        Math.abs(g.wallPerp - wallPerp) <= LAYOUT_PERP_TOL &&
        g.sideSign === sideSign
      ) {
        group = g;
        break;
      }
    }
    if (!group) {
      group = { angle, wallPerp, sideSign, items: [] };
      groups.push(group);
    }
    const entry = { tierDist, proj, half, sideX, sideY, slot: 0 };
    group.items.push(entry);
    return entry;
  });

  // 2. Per group: cluster into tiers (inner→outer), then greedy rows per tier.
  assignStaggerSlots(groups, {
    tierTol: LAYOUT_TIER_TOL,
    gap: LAYOUT_GAP / effectiveScale,
  });

  // 3. Re-place each measurement at its slot distance from the wall anchor.
  const pitchWorld = (fontPx + LAYOUT_ROW_PITCH_PX) / effectiveScale;
  // Base offset is screen-space (px/effectiveScale) for the look at/below the
  // zoom cap, but floored to a world distance so the first line still clears the
  // world-space wall thickness when zoomed in (px offset would otherwise stay
  // fixed while the wall grows).
  const baseWorld = Math.max(
    LAYOUT_BASE_MIN_M,
    LAYOUT_BASE_OFFSET_PX / effectiveScale
  );
  const gapWorld = labelGapWorld(fontPx, effectiveScale);
  const halfPerp = labelHalfHeightWorld(fontPx, effectiveScale);
  return measurements.map((m, index) => {
    const { slot, sideX, sideY, half } = entries[index];
    const dist = baseWorld + slot * pitchWorld;
    const sStart = [
      m.extensionStartFrom[0] + sideX * dist,
      m.extensionStartFrom[1] + sideY * dist,
    ];
    const sEnd = [
      m.extensionEndFrom[0] + sideX * dist,
      m.extensionEndFrom[1] + sideY * dist,
    ];
    // The label box, in world space, exactly where `drawMeasurementLabel` puts
    // it — the hit test reads this so clicking the TEXT selects the
    // measurement. Without it only the line is clickable, and the line is
    // `labelGapWorld` away: below ~60 px/m zoom that gap exceeds the hit
    // threshold and every label goes dead.
    const alongLen = Math.hypot(sEnd[0] - sStart[0], sEnd[1] - sStart[1]) || 1;
    return {
      ...m,
      offsetStart: sStart,
      offsetEnd: sEnd,
      extensionStartTo: sStart,
      extensionEndTo: sEnd,
      layoutSlot: slot,
      labelBox: {
        cx: (sStart[0] + sEnd[0]) / 2 + sideX * gapWorld,
        cy: (sStart[1] + sEnd[1]) / 2 + sideY * gapWorld,
        ux: (sEnd[0] - sStart[0]) / alongLen,
        uy: (sEnd[1] - sStart[1]) / alongLen,
        halfAlong: half,
        halfPerp,
      },
    };
  });
};
